import React from 'react'
import { useIntl } from 'react-intl'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import OfferPageStickyFooter from './OfferPageStickyFooter'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import { getHotelOfferDisplayedValue } from 'lib/offer/getOfferDisplayPricing'
import { useOfferPackagePrice } from 'hooks/Offers/useOfferPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import WalledContent from 'components/Common/WalledContent'

interface Props {
  lowestPricePackage: App.Package | App.BundlePackageWithPrice
  onScrollTo: () => void
  offer: App.Offer | App.OfferSummary
  visible: boolean
  airport?: App.Airport
}

function HotelOfferPageStickyFooter(props: Props) {
  const {
    onScrollTo,
    offer,
    visible,
    airport,
  } = props

  const intl = useIntl()
  const isForceBundled = !!offer.bundledWithFlightsOnly
  const flightPrice = offer.flightPrices[airport?.code ?? '']
  const pricePackage = useOfferPackagePrice(props.lowestPricePackage, offer, { flightPrice })
  const shouldShowRate = pricePackage.valueOffPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowValue = getHotelOfferDisplayedValue(isForceBundled, pricePackage.totalPrice, pricePackage.totalValue, !!offer.shouldDisplayValue)

  const viewOptionsCopy = intl.formatMessage({
    id: '2B69luklXK',
    defaultMessage: 'View options',
    description: 'CTA button text on hotel offer page sticky footer on the offer detail page',
  })

  const nightsWithFlightsFromLabel = intl.formatMessage(
    {
      id: '9pFgrgWLtj',
      defaultMessage: '{nights, plural, one {# night} other {# nights}} + flights from',
      description: 'Night count with flights and from label for hotel offer page sticky footer',
    },
    { nights: props.lowestPricePackage.duration },
  )
  const nightsFromLabel = intl.formatMessage(
    {
      id: 'lWcWYIcHc0',
      defaultMessage: '{nights, plural, one {# night} other {# nights}} from',
      description: 'Night count with from label for hotel offer page sticky footer',
    },
    { nights: props.lowestPricePackage.duration },
  )

  return (
    <OfferPageStickyFooter visible={visible} callToActionCopy={viewOptionsCopy} onCTAClick={onScrollTo}>
      <WalledContent enforced={offer.walledGarden}>
        <div>
          <PriceRowPriceCaption>
            {isForceBundled ? nightsWithFlightsFromLabel : nightsFromLabel}
          </PriceRowPriceCaption>
          <LuxPlusPriceGuard
            showMemberPrice={(pricePackage.totalMemberPrice ?? 0) > 0}
            fallbackPrice={<PriceRowPrice
              size="M"
              price={isForceBundled ? pricePackage.totalPriceWithFlights : pricePackage.totalPrice}
              saleUnit={isForceBundled ? 'package*' : pricePackage.saleUnit}
            />}
          >
            <PriceRowLuxPlusPrice
              size="M"
              memberPrice={isForceBundled ? pricePackage.totalMemberPriceWithFlights : pricePackage.totalMemberPrice}
              saleUnit={pricePackage.saleUnit}
              logoPlacement="leading"
            />
          </LuxPlusPriceGuard>
          {shouldShowValue && shouldShowRate && <PriceRowValueDiscountWithCaption
            size="L"
            originalValue={Math.ceil(isForceBundled ? pricePackage.totalValueWithFlights : pricePackage.totalValue)}
            discountPercentage={pricePackage.valueOffPercent}
          />}
        </div>
      </WalledContent>
    </OfferPageStickyFooter>
  )
}

export default withOfferPageState((state) => {
  return {
    airport: state.airport,
  }
}, HotelOfferPageStickyFooter)
